import { compact } from 'lodash/fp';

import { TriggerComputedField, TriggerEvent } from 'dashboard/models/trigger';
import { WorkflowOperatorKey } from 'dashboard/models/workflow';
import {
  ActualWorkflowStep,
  FakeWorkflowStepType,
  WorkflowStep,
  WorkflowStepMapOutput,
  WorkflowStepOutput,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { EVENT_NAME_TO_EVENT_TYPE, EventType } from 'lib/constants/events';
import { assertNever } from 'lib/utils/types';

export interface WorkflowStepTreeNode {
  parent: WorkflowStepTreeNode;
  previous: WorkflowStep;
  step: WorkflowStep;
  children: WorkflowStepTreeNode[];
}

export type TokensToNode = Record<string, WorkflowStepTreeNode>;

/**
 *
 * @param steps
 * Transforms a steps config into a steps config with parent nodes
 */
export const buildGraph = (steps?: WorkflowStep[]): TokensToNode => {
  if (steps == null || steps.length === 0) {
    return {};
  }

  const tokensToNode = {};
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  buildGraphHelper(null, steps, tokensToNode);
  return tokensToNode;
};

const buildGraphHelper = (
  parent: WorkflowStepTreeNode,
  steps: WorkflowStep[],
  tokensToNode: TokensToNode,
): WorkflowStepTreeNode[] => {
  return steps.map((step, i) => {
    const node: WorkflowStepTreeNode = {
      parent,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      previous: i === 0 ? null : steps[i - 1],
      step,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      children: null,
    };

    tokensToNode[step.token] = node;

    if (step.type === WorkflowStepType.Conditional || step.type === WorkflowStepType.Parallel) {
      node.children = step.branches
        .map((branch) => buildGraphHelper(node, branch, tokensToNode))
        .flat();
    } else if (step.type === WorkflowStepType.Loop) {
      node.children = buildGraphHelper(node, step.branch, tokensToNode).flat();
    }

    return node;
  });
};

/**
 * Finds a path to a node in a Workflows 2.0 Tree. Order not guaranteed. Skips conditional nodes as
 * we can't know the path to a node if it comes after a conditional at configuration time.
 */
export const findPath = (
  nodeToken: string,
  steps: (WorkflowStep | WorkflowStepOutput)[],
): WorkflowStep[] => {
  if (steps == null || steps.length === 0) {
    return [];
  }

  const path: WorkflowStep[] = [];
  const returnedPath = findPathHelper(nodeToken, steps, path, 0);

  return returnedPath ?? [];
};

const findPathHelper = (
  nodeToken: string,
  steps: (WorkflowStep | WorkflowStepOutput)[],
  path: WorkflowStep[],
  iterator: number,
): WorkflowStep[] | null => {
  if (iterator >= steps.length) {
    return null; // no more nodes left in this branch
  }

  const current = steps[iterator];
  if (current.token === nodeToken) {
    return path;
  }

  switch (current.type) {
    case WorkflowStepType.Placeholder:
    case WorkflowStepType.Output:
      // ignore these steps and continue iterating
      return findPathHelper(nodeToken, steps, path, iterator + 1);
    case FakeWorkflowStepType.MapOutput:
    case WorkflowStepType.Action:
    case WorkflowStepType.Module:
    case WorkflowStepType.Wait: {
      path.push(current);
      const newPath = findPathHelper(nodeToken, steps, [...path], iterator + 1);
      if (newPath == null) {
        path.pop();
        return null;
      }

      return newPath;
    }

    // Look in all conditional subtrees. If node is in subtree then return the path.
    // If not then skip subtree and move on
    case WorkflowStepType.Conditional: {
      const pathsWithNodeFound = compact(
        current.branches
          .filter((branch) => branch.length !== 0)
          .map((branch) => {
            path.push(current);
            const potentialPathWithNode = findPathHelper(nodeToken, branch, [...path], 0);
            path.pop();
            return potentialPathWithNode;
          }),
      );

      if (pathsWithNodeFound.length === 0) {
        path.push(current);
        const nextPath = findPathHelper(nodeToken, steps, [...path], iterator + 1);
        path.pop();
        return nextPath;
      } else {
        return Array.from(new Set(pathsWithNodeFound.flat()));
      }
    }

    // Look in all parallel subtrees. If node is in subtree then return the path.
    // If not then combine all subtrees and continue forward
    case WorkflowStepType.Parallel: {
      const pathsWithNodeFound = compact(
        current.branches
          .filter((branch) => branch.length !== 0)
          .map((branch) => {
            path.push(current);
            const potentialPathWithNode = findPathHelper(nodeToken, branch, [...path], 0);
            path.pop();
            return potentialPathWithNode;
          }),
      );

      // Node not found in any branches -- continue on but add in all the parallel nodes
      if (pathsWithNodeFound.length === 0) {
        path.push(current);
        const allAncestors = path.concat(
          flattenBranches(current.branches, [WorkflowStepType.Conditional]),
        );
        const nextPath = findPathHelper(nodeToken, steps, [...allAncestors], iterator + 1);
        path.pop();

        return nextPath;
      } else {
        return Array.from(new Set(pathsWithNodeFound.flat()));
      }
    }

    // Look in loop branch. If node is in subtree then return the path.
    // If not then skip subtree and move on.
    // TODO: Explore possibility of adding loop branch steps into path in the future.
    case WorkflowStepType.Loop: {
      path.push(current);
      const potentialPathWithNode = findPathHelper(nodeToken, current.branch, [...path], 0);
      path.pop();

      if (potentialPathWithNode) {
        return Array.from(new Set(potentialPathWithNode.flat()));
      } else {
        path.push(current);
        const nextPath = findPathHelper(nodeToken, steps, [...path], iterator + 1);
        path.pop();

        return nextPath;
      }
    }
    default:
      return assertNever(current);
  }
};

/**
 * @param branches
 * @param excludeBranchesForStepTypes
 * Flattens a branches array into a single array of nodes
 */

export const flattenBranches = (
  branches: WorkflowStep[][],
  excludeBranchesForStepTypes: (
    | WorkflowStepType.Conditional
    | WorkflowStepType.Parallel
    | WorkflowStepType.Loop
  )[] = [],
): WorkflowStep[] => {
  return branches.flatMap((branch) => flattenBranch(branch, excludeBranchesForStepTypes));
};

export const flattenBranch = (
  branch: WorkflowStep[],
  excludeBranchesForStepTypes: (
    | WorkflowStepType.Conditional
    | WorkflowStepType.Parallel
    | WorkflowStepType.Loop
  )[] = [],
): WorkflowStep[] => {
  return branch
    .filter(
      (step): step is ActualWorkflowStep | WorkflowStepMapOutput =>
        step.type !== WorkflowStepType.Placeholder,
    )
    .flatMap((node) => {
      switch (node.type) {
        case WorkflowStepType.Action:
        case WorkflowStepType.Module:
        case WorkflowStepType.Wait:
        case FakeWorkflowStepType.MapOutput:
          return node;
        case WorkflowStepType.Conditional:
          return excludeBranchesForStepTypes.includes(WorkflowStepType.Conditional)
            ? node
            : [node, ...flattenBranches(node.branches, excludeBranchesForStepTypes)];
        case WorkflowStepType.Parallel:
          return excludeBranchesForStepTypes.includes(WorkflowStepType.Parallel)
            ? node
            : [node, ...flattenBranches(node.branches, excludeBranchesForStepTypes)];
        case WorkflowStepType.Loop:
          return excludeBranchesForStepTypes.includes(WorkflowStepType.Loop)
            ? node
            : [node, ...flattenBranch(node.branch, excludeBranchesForStepTypes)];
        default:
          return assertNever(node);
      }
    });
};

export const eventTypeFor = (trigger: TriggerEvent): EventType => {
  return EVENT_NAME_TO_EVENT_TYPE[trigger.attributes['event-name']];
};

export const eventTypeForComputedFieldTrigger = (
  trigger: TriggerComputedField,
): EventType | null => {
  return trigger.attributes['config'].length
    ? EVENT_NAME_TO_EVENT_TYPE[trigger.attributes['config'][0]]
    : null;
};

export const isMultiValue = (operator: WorkflowOperatorKey) =>
  [
    WorkflowOperatorKey.In,
    WorkflowOperatorKey.NotIn,
    WorkflowOperatorKey.Contains,
    WorkflowOperatorKey.ContainsAnySubstring,
    WorkflowOperatorKey.ContainsNoSubstring,
    WorkflowOperatorKey.StartsWith,
    WorkflowOperatorKey.IncludesAll,
    WorkflowOperatorKey.IncludesNotAll,
    WorkflowOperatorKey.IncludesOnly,
    WorkflowOperatorKey.IncludesNone,
    WorkflowOperatorKey.IncludesExactly,
    WorkflowOperatorKey.IncludesNotExactly,
    WorkflowOperatorKey.AccountNameSubstringMatchAll,
    WorkflowOperatorKey.AccountNameSubstringMatchNotAll,
    WorkflowOperatorKey.AccountNameSubstringMatchAny,
    WorkflowOperatorKey.AccountNameSubstringMatchNone,
  ].includes(operator);

export const generateWorkflowToken = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

export const hasNoPossibilities = (operator: WorkflowOperatorKey) =>
  !operator ||
  [
    WorkflowOperatorKey.IsBlank,
    WorkflowOperatorKey.IsNotBlank,
    WorkflowOperatorKey.IsTruthy,
    WorkflowOperatorKey.IsFalsy,
  ].includes(operator);
